import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Loader2, XCircle } from 'lucide-react';
import { MeshThumb } from './MeshThumb';
import { isEarlierPass, passGroup, passOf, versionGroup, versionRootId } from './openaiBlenderApi';
import { meshSrc, meshStamp, pieceOf, runNumberOf, type Job3D } from './threeDApi';
import { fixedFor, type CatalogSwitch } from './catalogSwitch';
import { useLanguage } from '../../contexts/LanguageContext';
import { IS_DEMO } from '../../apiBase';

// EVERY MESH A THING HAS EVER HAD, under the one it is showing — for a part, and for the
// whole product, which are the two places in Phase 5 that hold a viewer over a history.
//
// SHARED BECAUSE THEY ARE THE SAME STRIP, and it was written twice. The part's grew rows
// per engine, one tile per run rather than per pass, and a label saying how many passes a
// run took; the product's stayed a flat row of "#89" because nobody edited it that day.
// Two copies of a component are two components the moment one of them is improved — and
// what makes them one is not a rule about editing both, it is this file.
//
// AND THE DATABASE SWITCH ON EVERY TILE, for both. It was an optional prop the product's panel
// never passed, which is how a whole product came to have no way into the database: `catalog`
// is required now, so a strip cannot be drawn without it.

export type RunGroup = { model: string; label: string; runs: Job3D[] };

// ONE ROW PER ENGINE: two models' runs, or a written run beside an old CAD upload, are two
// different things, and comparing them is a different question from comparing two runs of
// one of them.
//
// Grouped only when there is something to group: one engine draws exactly the row it always
// drew, unlabelled, which is also what CAD and lathe get.
//
// The row whose newest run is newest goes on top — `ready` arrives newest-first, so the
// order the models first appear in it is that order already. Within a row it is oldest to
// newest, so it reads left to right in the order the runs were made and the next one will
// appear at the end.
export function runGroups(
  ready: Job3D[], engineLabel: (job: Job3D) => string | null,
): RunGroup[] {
  // ONE TILE PER RUN. A run that made its model twice is one run with two meshes in it, and
  // the earlier ones are reached from the viewer rather than from a second thumbnail beside
  // the first — see isEarlierPass.
  const heads = ready.filter((j) => !isEarlierPass(j, ready));
  const by = new Map<string, Job3D[]>();
  for (const j of heads) {
    const seen = by.get(j.model);
    if (seen) seen.push(j);
    else by.set(j.model, [j]);
  }
  return [...by.entries()].map(([model, runs]) => ({
    model,
    // The raw model id rather than nothing, for a run the page has no name for: a row with
    // no heading in a stack of headed ones reads as a heading that failed to load.
    // No model id in its place in the demo, where engineLabel names none on purpose.
    label: engineLabel(runs[0]) || (IS_DEMO ? '' : model),
    runs: [...runs].reverse(),
  }));
}

// THE STRIP'S ORDER: the rows, flattened as they are drawn, which is the only order an
// arrow key may agree with. Left goes left, and off the end of one row is onto the start of
// the next.
export const runOrder = (groups: RunGroup[]): Job3D[] => groups.flatMap((g) => g.runs);

// WHICH MESH IS ON SCREEN, and which pass each run was last looked at on.
//
// One id held the mesh on screen and nothing held the pass: pick pass 1 of run A, open run B,
// come back to A, and A's thumbnail — which stands for its LAST pass — put pass 2 back up. The
// choice was forgotten the moment the viewer left the run. So every mesh shown is written down
// against its run (by the run's first pass, which a new pass landing does not change), and
// `showRun` — what a tile and the up and down keys call — opens the run where it was left. A run never
// opened opens on its last pass, as before. `setShownId` shows exactly the mesh it is given:
// a pass picked, a run just made, a repair's result.
//
// For the page's life only. It is how somebody is looking, not a fact about the manual.
export function useShownRun(ready: Job3D[]) {
  const [shownId, setShownId] = useState<number | null>(null);
  const shown = ready.find((j) => j.id === shownId) ?? ready[0] ?? null;
  const lastPass = useRef(new Map<number, number>());
  useEffect(() => {
    if (shown) lastPass.current.set(passGroup(shown, ready)[0].id, shown.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shown?.id]);
  const readyRef = useRef(ready);
  readyRef.current = ready;
  const showRun = useCallback((job: Job3D) => {
    const was = lastPass.current.get(passGroup(job, readyRef.current)[0].id);
    setShownId(was != null && readyRef.current.some((j) => j.id === was) ? was : job.id);
  }, []);
  return { shown, setShownId, showRun };
}

// WHICH FAILED RUN IS IN THE VIEWER'S PLACE, if one is — see FailedRunPanel. Beside useShownRun
// and not inside it: the panels read `shown` as a mesh (its file, its passes, its holes), and a
// run that made none is not one.
//
// A tile pressed shows it. And the newest failure shows ON ITS OWN when it is newer than every
// mesh — the press that made it is the last thing that happened, and a panel left as it was
// reads as a press that did nothing — until a mesh is picked (`leave`), which puts it back to
// the mesh and keeps it there until a newer failure. Not while a run is going: the pending tile
// is the news then.
export function useShownFailure(runs: Job3D[], busy: boolean) {
  const failures = useMemo(() => runs.filter((j) => !j.ready), [runs]);
  const [picked, setPicked] = useState<number | null>(null);
  const [leftAt, setLeftAt] = useState(0);
  const newestReady = Math.max(0, ...runs.filter((j) => j.ready).map((j) => j.id));
  const newest = failures.reduce<Job3D | null>((a, j) => (!a || j.id > a.id ? j : a), null);
  const auto = !busy && newest && newest.id > newestReady && newest.id > leftAt ? newest : null;
  const failure = picked != null ? failures.find((j) => j.id === picked) ?? auto : auto;
  const show = useCallback((job: Job3D) => setPicked(job.id), []);
  const leave = useCallback(() => {
    setPicked(null);
    setLeftAt(Math.max(0, ...runs.map((j) => j.id)));
  }, [runs]);
  return { failure, failures, show, leave };
}

// THE ARROW KEYS, ON THE THING THIS STRIP IS THE HISTORY OF — a part, or the whole product.
// UP AND DOWN STEP THROUGH ITS MODELS, in the order the strip draws them (one row per engine,
// one tile per run), opening each where it was left — see useShownRun. LEFT AND RIGHT STEP
// THROUGH THE PASSES of the model on screen, the toggle under the viewer. Changing part is a
// click on the parts strip, which gives up its own arrows on this phase for exactly that
// reason (see PartsPhase): one keyboard, two axes, and both of them about the mesh.
//
// Here rather than in the two panels because the order is this strip's, and because it needs
// something drawn to ask whether it is on screen. This app keeps every phase MOUNTED and hides
// the ones you are not on, and every phase after this one has its own stepper on left and
// right — so a listener that did not check would turn a pass here, unseen, while a sub-step
// turned there. `box` is on the strip's rule, which has no client rects while its phase is
// hidden. See useArrowSteps, which does the same.
//
// On the document, because the thing you are comparing is usually open over the whole
// page and the modal owns the pointer. Two guards earn their place: a field with focus
// keeps its arrows (the repair sentence sits inches away, and an arrow key that jumped
// the mesh instead of the caret would be a bug nobody could explain), and a modifier
// means the browser was asked, not us.
//
// Both WRAP. There are two or three of either, and the point of pressing the key is to flip
// between them and see what changed; stopping dead at the end of a three-item list is a rule
// that costs a keypress and buys nothing.
function useMeshArrows(
  ready: Job3D[],
  shown: Job3D | null,
  engineLabel: (job: Job3D) => string | null,
  onOpenRun: (job: Job3D) => void,
  onSelect: (job: Job3D) => void,
) {
  const box = useRef<HTMLSpanElement | null>(null);
  const strip = useMemo(
    () => runOrder(runGroups(ready, engineLabel)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [ready],
  );
  const live = useRef({ ready, shown, strip, onOpenRun, onSelect });
  live.current = { ready, shown, strip, onOpenRun, onSelect };
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const across = e.key === 'ArrowRight' ? 1 : e.key === 'ArrowLeft' ? -1 : 0;
      const down = e.key === 'ArrowDown' ? 1 : e.key === 'ArrowUp' ? -1 : 0;
      if ((!across && !down) || e.metaKey || e.ctrlKey || e.altKey) return;
      const el = document.activeElement as HTMLElement | null;
      if (el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable)) return;
      const own = box.current;
      if (!own || !own.isConnected || own.getClientRects().length === 0) return;
      const { ready: jobs, shown: on, strip: order } = live.current;
      if (!on) return;
      const group = passGroup(on, jobs);
      const list = across ? group : order;
      // The pass a version on screen belongs to is where left and right start from.
      const passId = versionRootId(on, jobs);
      const at = across
        ? group.findIndex((p) => p.id === passId)
        : order.findIndex((j) => group.some((p) => p.id === j.id));
      if (at < 0 || list.length < 2) return;
      e.preventDefault();
      const next = list[(at + (across || down) + list.length) % list.length];
      if (across) live.current.onSelect(next);
      else live.current.onOpenRun(next);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);
  return box;
}

// A RUN'S NAME UNDER ITS THUMBNAIL. Its number, and how many passes it took where it took
// more than one — "#95 (2 passes)". The number alone left a draft and its correction as two
// unrelated meshes a few ids apart.
//
// AND A COMPONENT CUT OUT OF A WHOLE PRODUCT CARRIES THAT PRODUCT'S NUMBER — see runNumberOf.
// The seat under "#154, from the whole product" and the chair under "#154" are one answer
// seen from two rows of the database, which is what they are; the seat under "#157" would be
// a run of a seat that nobody made.
export function passLabel(job: Job3D, t: (k: string) => string): string {
  const pass = passOf(job);
  // WHAT IT WAS CUT FROM, where the piece says. One that does not is left saying nothing
  // rather than guessed at: "from the whole product" under a washer cut from its hardware set
  // was exactly that guess.
  const cutFrom = pieceOf(job)?.from;
  const from = cutFrom === 'product' ? `, ${t('notice.3d.fromProduct')}`
    : cutFrom === 'set' ? `, ${t('notice.3d.fromSet')}` : '';
  return pass && pass.of > 1
    ? `#${runNumberOf(job)} (${t('notice.3d.passes').replace('{n}', String(pass.of))})${from}`
    : `#${runNumberOf(job)}${from}`;
}

// THE RUN THAT IS BEING MADE RIGHT NOW, drawn where it will land.
//
// A press used to change nothing here: the button counted seconds at the bottom of the page
// while the strip — the list this run is about to join — said nothing at all for a minute.
// The tile appears at once, in its engine's row, at the end where the newest goes, and holds
// a spinner until there is a mesh to put in it.
//
// NOT A JOB, because the pass being made has no id until it is saved, which is the same
// reason stopping a run needs a token the browser invents. So it is a shape of its own, and
// it is the only tile in the strip that stands for something that does not exist yet.
//
// AND IT OPENS WHAT DOES. A run saves each pass as it builds it, so by pass 4 there are three
// meshes to look at and the dashed square is the thing on the strip somebody is watching — it
// was the one tile that did nothing when pressed. Pressed, it shows the newest pass that has
// landed; until the first one does there is nothing behind it and it stays a spinner.
// A RUN THAT MADE NOTHING: a tile like the others, at its place in its engine's row, with no
// mesh to draw — pressed, its error and its prompt open where the viewer is (see FailedRunPanel).
function FailedThumb({ job, selected, onOpen }: { job: Job3D; selected: boolean; onOpen: () => void }) {
  const { t } = useLanguage();
  return (
    <figure className="flex w-24 shrink-0 flex-col items-center gap-1">
      <button
        type="button"
        onClick={onOpen}
        className={`flex h-24 w-24 items-center justify-center rounded-xl border-2 border-dashed bg-rose-50/60 transition-colors ${
          selected ? 'border-rose-500' : 'border-rose-200 hover:border-rose-400'}`}
      >
        <XCircle className="h-6 w-6 text-rose-300" />
      </button>
      <figcaption className={`text-center text-[11px] font-medium ${selected ? 'text-rose-600' : 'text-rose-400'}`}>
        {t('notice.3d.runFailedTile').replace('{n}', String(runNumberOf(job)))}
      </figcaption>
    </figure>
  );
}

function PendingThumb({ label, onOpen }: { label: string; onOpen?: () => void }) {
  return (
    <figure className="flex w-24 shrink-0 flex-col items-center gap-1">
      <button
        type="button"
        onClick={onOpen}
        disabled={!onOpen}
        className={`flex h-24 w-24 items-center justify-center rounded-xl border-2 border-dashed border-slate-300 bg-white transition-colors ${
          onOpen ? 'cursor-pointer hover:border-blue-400' : 'cursor-default'}`}
      >
        <Loader2 className="h-5 w-5 animate-spin text-slate-300" />
      </button>
      <figcaption className="text-center text-[11px] font-medium text-slate-400">
        {label}
      </figcaption>
    </figure>
  );
}

export function MeshRunStrip({
  ready, shown, engineLabel, title, onSelect, onOpenRun, onOpenPending, catalog, pending, repainting = false,
  failed = [], failedShown = null, onShowFailed,
}: {
  // Every READY run of this part or product, newest first — the order every jobs list
  // arrives in.
  ready: Job3D[];
  // AND THE RUNS THAT MADE NOTHING, each a tile in its engine's row at its place in time, the
  // one in the viewer's place lit (see useShownFailure). They were a line under the button,
  // which nobody saw once the run was over.
  failed?: Job3D[];
  failedShown?: Job3D | null;
  onShowFailed?: (job: Job3D) => void;
  // What the viewer above is showing, so the tile it came from is lit. Lit while ANY pass
  // of a run is on screen: stepping to a draft from inside the modal must not unlight the
  // tile that opened it.
  shown: Job3D | null;
  // What made a run — the row headings.
  engineLabel: (job: Job3D) => string | null;
  // The tooltip on a tile: how that run was made. Optional, since it is a sentence about
  // settings and not every caller has one to give.
  title?: (job: Job3D) => string | undefined;
  // Shows exactly this mesh — the tile switched into the database, the pass in flight, the
  // pass an arrow key stepped to.
  onSelect: (job: Job3D) => void;
  // Opens a run where it was last left — a tile pressed, or an arrow key. See useShownRun.
  onOpenRun: (job: Job3D) => void;
  // KEEPING A MESH AS THE THING'S MODEL — a part's or the whole product's. See useCatalogSwitch.
  catalog: CatalogSwitch;
  // A RUN IN FLIGHT — which engine is making it, what that engine is called, and the newest
  // pass it has already landed. The tile is drawn in that engine's row, or in a row of its own
  // where the engine has made nothing yet: a first run must not have to finish before the page
  // admits it is happening. `made` is what pressing it opens — null until a pass has landed.
  pending?: { model: string; label: string; made?: Job3D | null } | null;
  // AND PRESSING IT BEFORE A PASS HAS LANDED: the viewer goes back to waiting for the run.
  onOpenPending?: () => void;
  // EVERY TILE'S MESH IS BEING REWRITTEN — see MeshStage's `busy`. A spinner in each, so the
  // strip does not go on showing the materials the files no longer have.
  repainting?: boolean;
}) {
  const { t } = useLanguage();
  const arrows = useMeshArrows(ready, shown, engineLabel, onOpenRun, onSelect);
  const groups = useMemo(
    () => {
      const rows = runGroups(ready, engineLabel);
      // Each failure in its engine's row, in the order the runs were made.
      for (const f of failed) {
        const row = rows.find((g) => g.model === f.model);
        if (row) row.runs = [...row.runs, f].sort((a, b) => a.id - b.id);
        else rows.push({ model: f.model, label: engineLabel(f) || (IS_DEMO ? '' : f.model), runs: [f] });
      }
      if (!pending) return rows;
      // Its own row, at the top, where the engine has produced nothing yet — `ready` is
      // newest-first and so are the rows, and a run starting now is the newest thing here.
      return rows.some((g) => g.model === pending.model)
        ? rows
        : [{ model: pending.model, label: pending.label, runs: [] }, ...rows];
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [ready, failed, pending?.model],
  );
  if (!ready.length && !pending && !failed.length) return null;

  return (
    <>
      {/* A rule between the mesh and the row of them, and room either side of it. They are
          two different things to look at — one object, and the list it was chosen from —
          and four thumbnails a few pixels under the viewport read as more of the viewport.
          Short and centred: it separates two halves of one panel, not two panels. */}
      <span
        ref={arrows}
        role="separator"
        className="mx-auto mt-7 block h-px w-80 max-w-full bg-slate-200"
      />
      <div className="mt-7 flex flex-col items-center gap-5">
        {groups.map((group) => (
          <div key={group.model} className="w-full">
            {/* NAMED EVEN ALONE: the heading over the viewer names a model only where one made
                the run, so a CAD upload or a lathe turn on its own was named nowhere. Not in the
                demo, where no engine is named (see runGroups). */}
            {group.label && (
              <p className="mb-2 text-center text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                {group.label}
              </p>
            )}
            <div className="flex flex-wrap items-start justify-center gap-2">
              {group.runs.map((j) => !j.ready ? (
                <FailedThumb
                  key={j.id}
                  job={j}
                  selected={failedShown?.id === j.id}
                  onOpen={() => onShowFailed?.(j)}
                />
              ) : repainting ? (
                <PendingThumb key={j.id} label={passLabel(j, t)} />
              ) : pending?.made && passGroup(j, ready).some((p) => p.id === pending.made?.id) ? (
                // THE RUN STILL GOING: its tile is the waiting one, the pass it has landed under the
                // spinner — not a tile of its own beside the wait.
                <MeshThumb
                  key={j.id}
                  src={meshSrc(pending.made.id, meshStamp(pending.made))}
                  selected={shown?.id === pending.made.id && !failedShown}
                  inCatalog={false}
                  working
                  label={t('notice.3d.generatingShort')}
                  onSelect={() => onSelect(pending.made as Job3D)}
                />
              ) : (() => {
                // THE DATABASE IS PER PASS, and one tile is a whole run. Its mark is lit when ANY
                // pass of the run is kept — several may be — and its switch acts on passes, never
                // on "the run": the pass on screen, when this run is the one shown; else, where
                // passes are kept, all of them come out; else the run's last pass goes in.
                // Every VERSION of every pass counts, a version being a mesh that can be kept too.
                const runPasses = passGroup(j, ready);
                const meshes = runPasses.flatMap((p) => versionGroup(p, ready));
                const kept = meshes.filter((p) => p.inCatalog);
                const onScreen = shown && meshes.find((p) => p.id === shown.id);
                return (
                <MeshThumb
                  key={j.id}
                  src={meshSrc(j.id, meshStamp(j))}
                  selected={!!onScreen && !failedShown}
                  inCatalog={kept.length > 0}
                  label={passLabel(j, t)}
                  title={title?.(j)}
                  onSelect={() => onOpenRun(j)}
                  onToggleCatalog={() => {
                    if (onScreen) {
                      catalog.onToggle(onScreen, !onScreen.inCatalog);
                    } else if (kept.length) {
                      kept.forEach((p) => catalog.onToggle(p, false));
                    } else {
                      // SWITCHING ONE ON SHOWS IT, so "which is kept" and "which am I looking
                      // at" agree at the moment the first is settled.
                      onSelect(j);
                      catalog.onToggle(j, true);
                    }
                  }}
                  catalogBusy={meshes.some((p) => catalog.busyId === p.id)}
                  catalogFixed={fixedFor(catalog, j)}
                />
                );
              })())}
              {/* AT THE END OF ITS OWN ENGINE'S ROW, which is where it will appear when it
                  lands — so the tile does not jump across the strip the moment it becomes a
                  mesh. */}
              {pending?.model === group.model && !pending.made && (
                <PendingThumb
                  label={t('notice.3d.generatingShort')}
                  onOpen={pending.made ? () => onSelect(pending.made as Job3D) : onOpenPending}
                />
              )}
            </div>
          </div>
        ))}
      </div>
    </>
  );
}
